import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import Button from "@mui/material/Button";
import StyledDataGrid from "../../../../components/DataGrid";
import { exportExcelWb, XlsxColumn } from "../../../../utils/excel";
import { Typography } from "@mui/material";
import moment from "moment";

const dateFormats = [
  moment.ISO_8601,
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  "M/D/YYYY",
  "MM-DD-YYYY",
  "M-D-YYYY",
];

const formatHistoryDate = (value) => {
  if (!value) return "";

  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    const parsed = moment.utc([year, Number(month) - 1, day]);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "";
  }

  if (typeof value === "string") {
    const trimmedValue = value.trim();
    const parsed = moment.utc(trimmedValue, dateFormats, true);

    if (parsed.isValid()) {
      return parsed.format("MM/DD/YYYY");
    }

    const nativeDate = new Date(trimmedValue);
    return Number.isNaN(nativeDate.getTime())
      ? ""
      : moment.utc(nativeDate).format("MM/DD/YYYY");
  }

  const parsed = moment.utc(value);

  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "";
};

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiDataGrid-footerContainer": {
    display: "none",
  },
}));

const columns = [
  {
    field: "decisionDate",
    headerName: "Decision Date",
    flex: 1,
    renderCell: (params) => formatHistoryDate(params.value),
  },
  {
    field: "quarter",
    headerName: "CAB info",
    flex: 1,
  },
  {
    field: "lineOfBusiness",
    headerName: "Line of Business",
    flex: 1.5,
  },
  {
    field: "adoptionDecision",
    headerName: "Adoption Decision",
    flex: 2,
    renderCell: (params) =>
      params.value ? params.value.replaceAll("_", " ") : "",
  },
  {
    field: "enforcementDate",
    headerName: "Enforcement Date",
    flex: 1,
    renderCell: (params) => formatHistoryDate(params.value),
  },
  {
    field: "approver",
    headerName: "Approver",
    minWidth: 200,
    flex: 2,
  },
  {
    field: "comments",
    headerName: "Comments",
    flex: 2,
  },
];

function HistoryModal({
  open,
  handleClose,
  historyModalData,
  historyPolicyData,
}) {
  const matchedPolicies = useMemo(
    () =>
      historyPolicyData.filter(
        (item) => item.policyNumber === historyModalData?.policyNumber
      ),
    [historyModalData?.policyNumber, historyPolicyData]
  );
  const policyNumber = historyModalData?.policyNumber
    ? historyModalData?.policyNumber
    : "";
  const policyTitle = historyModalData?.policyTitle
    ? historyModalData?.policyTitle
    : "";
  const policyKey = historyModalData?.policyKey
    ? historyModalData?.policyKey
    : "";
  const hpKey =
    historyModalData?.clientAdoptionDecisions?.[0]?.healthPlanKey || "";
  const lobName = ""; //Right now lobname is blank.

  const [historyList, setHistoryList] = useState([]);
  useEffect(() => {
    // Combine quarter and clientAdoptionDecisions for each matched policy
    let clientadoptionData = matchedPolicies.map((policy) =>
      (policy.clientAdoptionDecisions || []).map((cad) => ({
        ...cad,
        quarter: policy.quarter,
      }))
    );

    if (historyModalData && clientadoptionData.length > 0) {
      const clientAdoptionDecisions = clientadoptionData.flat();

      var returnHistoryList = clientAdoptionDecisions.map((cad) => {
        return {
          id: cad.clientAdoptionDecisionKey,
          decisionDate: cad.decisionDate,
          lineOfBusiness:
            cad.lineOfBusinessConfigurationDto
              ?.lineOfBusinessConfigurationDisplayName,
          quarter: cad.quarter,
          adoptionDecision: cad.adoptionDecisionStatusDesc,
          enforcementDate: cad.enforcementDate,
          approver: cad.approver,
          comments: cad.coverageCriteriaVarianceCommentText,
        };
      });
      setHistoryList(returnHistoryList);
    } else {
      setHistoryList([]);
    }
  }, [historyModalData, matchedPolicies]);

  const exportTable = () => {
    const columns = [
      new XlsxColumn("Decision Date", "decisionDate", 30),
      new XlsxColumn("Line of Business", "lineOfBusiness", 30),
      new XlsxColumn("Adoption Decision", "adoptionDecisionStatusDesc", 30),
      new XlsxColumn("Enforcement Date", "enforcementDate", 30),
      new XlsxColumn("Approver", "approver", 30),
      new XlsxColumn("Comments", "coverageCriteriaVarianceCommentText", 30),
    ];

    const rows = historyList.map((hl) => ({
      decisionDate: formatHistoryDate(hl.decisionDate),
      lineOfBusiness: hl.lineOfBusiness,
      approver: hl.approver,
      adoptionDecisionStatusDesc: hl.adoptionDecision
        ? hl.adoptionDecision.replaceAll("_", " ")
        : "",
      enforcementDate: formatHistoryDate(hl.enforcementDate),
      coverageCriteriaVarianceCommentText: hl.comments,
    }));

    exportExcelWb(`${hpKey}_${policyKey}_${lobName}`, columns, rows);
  };

  const getRowId = (row) => row.id;
  return (
    <BootstrapDialog
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogTitle sx={{ m: 0, p: 1, pr: 5 }} id="customized-dialog-title">
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h5" fontWeight="bold" noWrap>
              Policy History Details
            </Typography>
            <Typography variant="subtitle1" noWrap>
              {policyNumber} {policyTitle}
            </Typography>
          </div>

          <Button variant="outlined" onClick={exportTable}>
            Export
          </Button>
        </div>
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <StyledDataGrid
          autoHeight
          rows={historyList}
          columns={columns}
          getRowId={getRowId}
        />
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </BootstrapDialog>
  );
}

export default HistoryModal;
